import type { QuestionKindCapabilities } from '@/shared-question-model';

import type { SpokenAffordances } from './shared-responder-v2-core-handsfree.types';
import type { ThreadBehaviour } from './shared-responder-v2-core-thread-behaviour';
import type { ResponderTurnPrompt } from './shared-responder-v2-core-thread.types';

/**
 * Derived from the shared capability table rather than stated per kind, so all
 * ten existing kind files stay byte-identical and an eleventh kind gets a
 * sensible spoken shape for free.
 *
 * `supportsLiveDistribution` is the load-bearing one and reads oddly, so: it is
 * true for exactly the three kinds whose answer is one instant pick (choice,
 * image choice, rating) — a distribution is only meaningful for a single
 * categorical or scalar value. Those are also exactly the kinds the kernel
 * already carries forward on `selectShouldAutoAdvance`, so hands-free must not
 * additionally wait on a spoken Continue for them or the responder would have
 * to confirm an answer that already advanced.
 */
export function deriveSpokenAffordances(
  capabilities: QuestionKindCapabilities
): SpokenAffordances {
  return {
    announcesOptions: capabilities.hasOptionList,
    draftsSpokenAnswer: capabilities.acceptsFreeText,
    awaitsSpokenContinue: !capabilities.supportsLiveDistribution,
    extractsSpokenPick: false,
  };
}

export function resolveSpokenAffordances(
  behaviour: ThreadBehaviour
): SpokenAffordances {
  return {
    ...deriveSpokenAffordances(behaviour),
    ...behaviour.spokenAffordances,
  };
}

/**
 * One more builder layer over `createThreadBehaviour`, for a kind that needs
 * genuinely conversational behaviour: it takes a built behaviour and returns a
 * new one, merging `spokenAffordances` a level deeper than a plain spread would
 * so an override of one affordance does not silently drop the other two.
 *
 * Deliberately not a class hierarchy — `createThreadBehaviour` is already a
 * spread over defaults, and `super()` ordering would buy nothing here.
 */
export function createConversationalThreadBehaviour(
  base: ThreadBehaviour,
  overrides: Partial<ThreadBehaviour>
): ThreadBehaviour {
  return {
    ...base,
    ...overrides,
    spokenAffordances: {
      ...base.spokenAffordances,
      ...overrides.spokenAffordances,
    },
  };
}

/**
 * The spoken shape of a pending follow-up, from the prompt resolved by
 * `resolvePickedFollowUpPrompt`. A follow-up the responder picks from is read out with its
 * options and waits for a match; every other one is answered as prose, drafted live until Continue.
 */
export function resolveFollowUpSpokenAffordances(
  pickedPrompt: ResponderTurnPrompt | null
): SpokenAffordances {
  const isPick = pickedPrompt !== null;

  return {
    announcesOptions: isPick,
    draftsSpokenAnswer: !isPick,
    awaitsSpokenContinue: true,
    extractsSpokenPick: isPick,
  };
}
